import React, { useState, useRef } from "react";
import { ChatButton } from "./ChatButton";
import { ChatDialog } from "./ChatDialog";
import { ChatMessageData } from "./ChatWindow";
import XanoAPI from "../../lib/XanoAPI";

interface AIChatbotProps {
  onSendMessage?: (message: string, threadId?: string) => Promise<string>;
  variant?: "floating" | "footer";
}

export function AIChatbot({ onSendMessage, variant = "floating" }: AIChatbotProps) {
  const api = XanoAPI.getInstance();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Store thread ID for conversation continuity (even though UI is ephemeral)
  const threadIdRef = useRef<string | null>(null);

  const handleSendMessage = async (message: string) => {
    console.log("💬 Chat: Sending message:", message);
    console.log("🧵 Current thread ID:", threadIdRef.current || "none (new conversation)");
    console.log("📊 Current message count:", messages.length);
    
    // Validate message length before sending
    if (message.length > 2000) {
      const errorMessage: ChatMessageData = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "Your message is too long. Please keep it under 2000 characters and try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      return;
    }
    
    // Add user message immediately
    const userMessage: ChatMessageData = {
      id: `user-${Date.now()}`,
      role: "user",
      content: message,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      let response: string;
      let newThreadId: string;

      if (onSendMessage) {
        // Use custom handler if provided
        console.log("💬 Chat: Using custom message handler");
        response = await onSendMessage(message, threadIdRef.current || undefined);
        // Note: Custom handler should return thread_id if needed
      } else {
        // Use XanoAPI method
        console.log("=".repeat(80));
        console.log("💬 CHAT COMPONENT: Calling XanoAPI.sendChatMessage");
        console.log("=".repeat(80));
        console.log("📝 User message:", message);
        console.log("🧵 Current thread ID:", threadIdRef.current || "none (starting new conversation)");
        console.log("🔐 Is authenticated:", api.isAuthenticated());
        console.log("=".repeat(80));
        
        // TEMPORARY FIX: Always start fresh conversations to avoid context window issues
        // The backend accumulates too much history including full client data objects
        // Even without thread_id, the backend seems to include previous function call outputs
        // TODO: Backend needs to:
        //   1. Implement conversation summarization
        //   2. Truncate or exclude full client data objects from history
        //   3. Only include essential context, not entire data structures
        // 
        // For now, we never send thread_id to force fresh starts
        // This means each message is independent, but prevents context window errors
        const threadIdToUse: string | undefined = undefined; // Always start fresh
        
        console.log("🧵 Using thread ID: none (always fresh start)");
        console.log("📊 Reason: Backend accumulates too much history, forcing fresh start to prevent context window errors");
        console.log("⚠️ Note: This means the AI won't remember previous messages in this session");
        
        const apiResponse = await api.sendChatMessage(
          message,
          threadIdToUse
        );
        
        response = apiResponse.result;
        newThreadId = apiResponse.thread_id;
        
        console.log("=".repeat(80));
        console.log("✅ CHAT COMPONENT: Received API response");
        console.log("=".repeat(80));
        console.log("📝 Response length:", response.length, "characters");
        console.log("📝 Response preview:", response.substring(0, 100) + (response.length > 100 ? "..." : ""));
        console.log("🧵 New thread ID:", newThreadId);
        console.log("=".repeat(80));
        
        // Don't store thread ID - we're always starting fresh to avoid context window issues
        // The backend accumulates too much data even with threads
        // threadIdRef.current = newThreadId; // Commented out - always start fresh
        console.log("💾 Chat: Received thread ID but not storing (always starting fresh):", newThreadId);
      }

      // Add AI response
      const aiMessage: ChatMessageData = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: response,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error("=".repeat(80));
      console.error("❌ CHAT ERROR DEBUG");
      console.error("=".repeat(80));
      console.error("❌ Error type:", error instanceof Error ? error.constructor.name : typeof error);
      console.error("❌ Error message:", error instanceof Error ? error.message : String(error));
      if (error instanceof Error && error.stack) {
        console.error("❌ Error stack:", error.stack);
      }
      console.error("🧵 Current thread ID:", threadIdRef.current);
      console.error("=".repeat(80));
      
      // Handle specific error types
      let errorContent = "Sorry, I encountered an error. Please try again.";
      let shouldResetThread = false;
      
      if (error instanceof Error) {
        const errorMsg = error.message.toLowerCase();
        
        if (errorMsg.includes("context window") || errorMsg.includes("exceeds")) {
          errorContent = "⚠️ The conversation has become too long. I've automatically reset the conversation. Please try your question again.";
          shouldResetThread = true; // Reset thread to prevent further context window issues
          console.warn("⚠️ Context window exceeded - automatically resetting thread ID");
          console.warn("⚠️ This happens when the backend accumulates too much conversation history");
        } else if (errorMsg.includes("authenticated") || errorMsg.includes("401")) {
          errorContent = "You need to be logged in to use the chat. Please sign in and try again.";
        } else if (errorMsg.includes("network") || errorMsg.includes("fetch")) {
          errorContent = "Network error. Please check your connection and try again.";
        } else {
          errorContent = `Sorry, I encountered an error: ${error.message}`;
        }
      }
      
      // Reset thread if context window exceeded
      if (shouldResetThread) {
        threadIdRef.current = null;
        console.log("🔄 Thread ID reset due to context window error");
      }
      
      // Add error message
      const errorMessage: ChatMessageData = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: errorContent,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => {
    console.log("💬 Chat: Opening chatbot");
    setIsOpen(true);
  };

  const handleClose = () => {
    console.log("💬 Chat: Closing chatbot");
    setIsOpen(false);
    // Clear messages when closing (ephemeral behavior)
    // Thread ID is kept in ref for backend continuity, but UI starts fresh
    setMessages([]);
    // Reset thread ID to start completely fresh next time (truly ephemeral)
    // This helps avoid context window issues with long conversations
    threadIdRef.current = null;
    console.log("🔄 Chat: Thread ID reset for fresh start");
  };

  const handleClearConversation = () => {
    console.log("=".repeat(80));
    console.log("🔄 CHAT: Clearing conversation and resetting thread");
    console.log("=".repeat(80));
    console.log("📊 Messages before clear:", messages.length);
    console.log("🧵 Thread ID before clear:", threadIdRef.current);
    setMessages([]);
    threadIdRef.current = null;
    console.log("✅ Chat: Conversation cleared, ready for fresh start");
    console.log("🧵 Thread ID after clear: null");
    console.log("=".repeat(80));
  };

  return (
    <>
      <ChatButton onClick={handleOpen} isOpen={isOpen} variant={variant} />
      <ChatDialog
        isOpen={isOpen}
        onClose={handleClose}
        messages={messages}
        onSendMessage={handleSendMessage}
        onClearConversation={handleClearConversation}
        isLoading={isLoading}
      />
    </>
  );
}

